import { memo, type MouseEvent, type ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import FacebookIcon from "@mui/icons-material/Facebook";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import type { WhatsAppBusinessPhoneContact } from "../services/whatsappBusinessSetupService";

export interface MetaOptionCardProps {
  isMetaFullyConnected: boolean;
  isMetaCardHighlighted: boolean;
  shouldDimMetaCard: boolean;
  isUsingMetaOption: boolean;
  /** Extensão FluxZap assinada — necessária para vincular Meta. */
  isFluxZapExtensionActive?: boolean;
  metaConnectionMode: "embedded" | "manual";
  hasManualWabaLookupSucceeded: boolean;
  partnerIdCopied: boolean;
  isManualWabaInputVisible: boolean;
  manualValidationError: string | null;
  manualWabaId: string;
  isLoadingManualPhoneContacts: boolean;
  manualPhoneLookupError: string | null;
  manualPhoneContacts: WhatsAppBusinessPhoneContact[];
  selectedManualPhoneContactId: string | null;
  hasMetaEmbeddedSignupConfig: boolean;
  isSecureContextForMeta: boolean;
  metaEmbeddedSignupError: string | null;
  isMetaConnected: boolean;
  metaConnectionWabaId?: string | null;
  showMetaDisconnectButton: boolean;
  isSavingMetaConnection: boolean;
  isSavingOption: boolean;
  isMetaEmbeddedSignupReady: boolean;
  isMetaEmbeddedSignupLoading: boolean;
  onMetaConnectionModeChange: (
    event: MouseEvent<HTMLElement>,
    mode: "embedded" | "manual" | null,
  ) => void;
  onCopyPartnerId: () => void;
  onManualWabaIdChange: (value: string) => void;
  onShowManualWabaInput: () => void;
  onSelectManualPhoneContact: (id: string) => void;
  onDisconnectMetaAccount: () => void;
  onManualLinking: () => void;
  onConnectMetaClick: () => void;
}

const MetaOptionCard = memo((props: MetaOptionCardProps) => {
  const theme = useTheme();
  const {
    isMetaFullyConnected,
    isMetaCardHighlighted,
    shouldDimMetaCard,
    isUsingMetaOption,
    isFluxZapExtensionActive = false,
    metaConnectionMode,
    hasManualWabaLookupSucceeded,
    partnerIdCopied,
    isManualWabaInputVisible,
    manualValidationError,
    manualWabaId,
    isLoadingManualPhoneContacts,
    manualPhoneLookupError,
    manualPhoneContacts,
    selectedManualPhoneContactId,
    hasMetaEmbeddedSignupConfig,
    isSecureContextForMeta,
    metaEmbeddedSignupError,
    isMetaConnected,
    metaConnectionWabaId,
    showMetaDisconnectButton,
    isSavingMetaConnection,
    isSavingOption,
    isMetaEmbeddedSignupReady,
    isMetaEmbeddedSignupLoading,
    onMetaConnectionModeChange,
    onCopyPartnerId,
    onManualWabaIdChange,
    onShowManualWabaInput,
    onSelectManualPhoneContact,
    onDisconnectMetaAccount,
    onManualLinking,
    onConnectMetaClick,
  } = props;

  return (
    <Box
      sx={{
        flex: 1,
        p: 2.5,
        borderRadius: 2,
        border: "2px solid",
        borderColor: isMetaFullyConnected
          ? alpha("#1877F2", 0.65)
          : isMetaCardHighlighted
            ? alpha("#1877F2", 0.45)
            : "divider",
        background: isMetaFullyConnected
          ? `linear-gradient(145deg, ${alpha("#1877F2", 0.12)} 0%, ${alpha("#00C6FF", 0.1)} 100%)`
          : isMetaCardHighlighted
            ? `linear-gradient(145deg, ${alpha("#1877F2", 0.06)} 0%, ${alpha("#00C6FF", 0.05)} 100%)`
            : "background.default",
        boxShadow: isMetaFullyConnected
          ? `0 0 0 3px ${alpha("#1877F2", 0.2)}, 0 8px 24px ${alpha("#1877F2", 0.18)}`
          : isMetaCardHighlighted
            ? `0 0 0 2px ${alpha("#1877F2", 0.12)}, 0 4px 16px ${alpha("#1877F2", 0.1)}`
            : "none",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minHeight: { md: 360 },
        opacity: shouldDimMetaCard ? 0.55 : 1,
        transition:
          "box-shadow 0.25s ease, border-color 0.25s ease, background 0.25s ease, opacity 0.2s ease",
      }}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Vincular conta Meta Business
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Recomendado para produção. Vincule a conta Meta para usar
          números autorizados e ativos reais da clínica no CRM.
        </Typography>
        {!isFluxZapExtensionActive ? (
          <Alert severity="warning" sx={{ mt: 0.5 }}>
            Assine a extensão FluxZap para habilitar a vinculação com sua conta
            Meta / WhatsApp Business.
          </Alert>
        ) : null}
      </Stack>

      <Box
        sx={{
          p: 2,
          borderColor: isMetaFullyConnected ? alpha("#1877F2", 0.45) : isMetaCardHighlighted ? alpha("#1877F2", 0.3) : "divider",
          background: isMetaFullyConnected ? `linear-gradient(135deg, ${alpha("#1877F2", 0.1)} 0%, ${alpha("#00C6FF", 0.08)} 100%)` : isMetaCardHighlighted
            ? `linear-gradient(135deg, ${alpha("#1877F2", 0.05)} 0%, ${alpha("#00C6FF", 0.04)} 100%)`
            : "background.paper",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isMetaFullyConnected
                  ? "linear-gradient(135deg, #1877F2 0%, #00C6FF 100%)"
                  : isMetaCardHighlighted
                    ? "linear-gradient(135deg, #5B9BD5 0%, #7EC8E3 100%)"
                    : "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
                boxShadow:
                  isMetaFullyConnected || isMetaCardHighlighted
                    ? "0 4px 12px rgba(24, 119, 242, 0.25)"
                    : "none",
              }}
            >
              <FacebookIcon
                sx={{
                  fontSize: 28,
                  color:
                    isMetaFullyConnected || isMetaCardHighlighted
                      ? "common.white"
                      : "grey.600",
                }}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" fontWeight={600} noWrap>
                  Conta Meta Business
                </Typography>
                <Chip
                  size="small"
                  label={
                    isMetaFullyConnected
                      ? "Conectado"
                      : isUsingMetaOption
                        ? "Opção selecionada"
                        : "Não conectado"
                  }
                  color={
                    isMetaFullyConnected
                      ? "success"
                      : isUsingMetaOption
                        ? "primary"
                        : "default"
                  }
                  variant={
                    isMetaFullyConnected || isUsingMetaOption
                      ? "filled"
                      : "outlined"
                  }
                  sx={{ fontWeight: 500 }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {isMetaFullyConnected
                  ? "Sua conta Meta está vinculada. O canal será gerenciado com integração real."
                  : isUsingMetaOption
                    ? "Opção Meta selecionada. Conclua por Embedded Signup ou preencha manualmente para vincular o WABA."
                    : "Escolha o tipo de vinculação: automática com Meta ou manual com dados da conta."}
              </Typography>
            </Box>
          </Stack>

          <ToggleButtonGroup
            value={metaConnectionMode}
            exclusive
            fullWidth
            size="small"
            disabled={!isFluxZapExtensionActive}
            onChange={onMetaConnectionModeChange}
            aria-label="Tipo de vinculação Meta"
            sx={{ bgcolor: alpha(theme.palette.common.black, 0.02) }}
          >
            <ToggleButton value="embedded">Via Meta (automático)</ToggleButton>
            <ToggleButton value="manual">Vinculação manual</ToggleButton>
          </ToggleButtonGroup>

          {metaConnectionMode === "embedded" ? (
            <Typography variant="caption" color="text.secondary">
              Inicia o fluxo oficial da Meta (Embedded Signup) para autorizar
              e retornar os dados da conta automaticamente.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {!hasManualWabaLookupSucceeded ? (
                <Box
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.info.main, 0.3),
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: alpha(theme.palette.info.main, 0.08),
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.info.main, 0.2),
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <FacebookIcon sx={{ fontSize: 16, color: "info.main" }} />
                    <Typography variant="caption" fontWeight={700} color="info.main">
                      Pré-requisitos — vinculação manual
                    </Typography>
                  </Box>

                  <Stack spacing={0} divider={<Box sx={{ borderTop: "1px dashed", borderColor: alpha(theme.palette.info.main, 0.12) }} />}>
                    {(
                      [
                        { step: "1", text: <>Tenha uma <strong>conta Meta Business</strong> ativa.</> },
                        {
                          step: "2",
                          text: <>
                            Vá em <strong>Configurações</strong> {">"} <strong>Parceiros</strong> {">"} <strong>Adicionar</strong> {">"}{" "}
                            <strong>Conceder acesso a um parceiro</strong>. Use o ID:{" "}
                            <Chip
                              size="small"
                              color={partnerIdCopied ? "success" : "info"}
                              variant="filled"
                              label="933366781827729"
                              icon={
                                partnerIdCopied
                                  ? <DoneIcon sx={{ fontSize: "0.7rem !important" }} />
                                  : <ContentCopyIcon sx={{ fontSize: "0.7rem !important" }} />
                              }
                              onClick={onCopyPartnerId}
                              title="Clique para copiar"
                              sx={{
                                fontWeight: 700,
                                height: 18,
                                fontSize: "0.65rem",
                                letterSpacing: 0.2,
                                verticalAlign: "middle",
                                mx: 0.25,
                                cursor: "pointer",
                                transition: "background-color 0.2s ease",
                                "& .MuiChip-label": { px: 0.75 },
                                "& .MuiChip-icon": { ml: 0.5 },
                              }}
                            />
                            <Link href="https://business.facebook.com/latest/settings/partners" target="_blank" rel="noopener noreferrer" variant="caption" underline="hover" sx={{ ml: 0.5, fontWeight: 600 }}>
                              Abrir
                            </Link>
                          </>,
                        },
                        {
                          step: "3",
                          text: <>
                            Copie sua <strong>Identificação do portfólio empresarial</strong> e informe como WABA ID.{" "}
                            <Link href="https://business.facebook.com/latest/settings/business_info" target="_blank" rel="noopener noreferrer" variant="caption" underline="hover" sx={{ fontWeight: 600 }}>
                              Abrir
                            </Link>
                          </>,
                        },
                      ] as { step: string; text: ReactNode }[]
                    ).map(({ step, text }) => (
                      <Stack key={step} direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 0.875 }}>
                        <Box
                          sx={{
                            minWidth: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: alpha(theme.palette.info.main, 0.15),
                            color: "info.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.6rem",
                            flexShrink: 0,
                            mt: 0.05,
                          }}
                        >
                          {step}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                          {text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  {!isManualWabaInputVisible ? (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: alpha(theme.palette.info.main, 0.04),
                        borderTop: "1px solid",
                        borderColor: alpha(theme.palette.info.main, 0.2),
                      }}
                    >
                      <Button
                        type="button"
                        size="small"
                        variant="contained"
                        disableElevation
                        disabled={!isFluxZapExtensionActive}
                        onClick={onShowManualWabaInput}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 1.5,
                        }}
                      >
                        Seguir →
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              ) : null}

              {isManualWabaInputVisible ? (
                <Box sx={{ p: 1, borderColor: "divider" }}>
                  <Stack spacing={2}>
                    {manualValidationError ? (
                      <Typography variant="body2" color="text.secondary">
                        Informe o WABA ID para vincular manualmente sem abrir o login Meta.
                      </Typography>
                    ) : null}

                    <TextField
                      variant="filled"
                      required
                      label="WABA ID"
                      placeholder="Ex.: 123456789012345"
                      value={manualWabaId}
                      disabled={!isFluxZapExtensionActive}
                      onChange={(event) => onManualWabaIdChange(event.target.value)}
                    />
                  </Stack>
                </Box>
              ) : null}
              {isManualWabaInputVisible && isLoadingManualPhoneContacts ? (
                <Typography variant="caption" color="text.secondary">
                  Buscando números de telefone...
                </Typography>
              ) : null}
              {isManualWabaInputVisible && manualPhoneLookupError ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {manualPhoneLookupError}
                </Alert>
              ) : null}
              {isManualWabaInputVisible && !isLoadingManualPhoneContacts && manualPhoneLookupError == null && manualWabaId.trim() && manualPhoneContacts.length === 0 ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Nenhum número encontrado para este WABA ID.
                </Alert>
              ) : null}
              {isManualWabaInputVisible && manualPhoneContacts.length > 0 ? (
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Números encontrados
                    </Typography>
                    <Chip size="small" label="Selecione o principal" color="primary" variant="outlined" />
                  </Stack>
                  <Stack spacing={0.75}>
                    {manualPhoneContacts.map((phoneContact) => {
                      const isSelected = selectedManualPhoneContactId === phoneContact.id;
                      return (
                        <Paper
                          key={phoneContact.id}
                          variant="outlined"
                          component="button"
                          type="button"
                          onClick={() => onSelectManualPhoneContact(phoneContact.id)}
                          sx={{
                            p: 1,
                            width: "100%",
                            borderRadius: 1.5,
                            textAlign: "left",
                            cursor: "pointer",
                            borderColor: isSelected ? "primary.main" : "divider",
                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : "background.paper",
                            boxShadow: isSelected ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}` : "none",
                            transition:
                              "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            },
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: alpha("#25D366", isSelected ? 0.2 : 0.14),
                                  color: "#25D366",
                                  flexShrink: 0,
                                }}
                              >
                                <WhatsAppIcon sx={{ fontSize: 17 }} />
                              </Box>
                              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                  {phoneContact.displayPhoneNumber}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }} noWrap>
                                  ID: {phoneContact.id}
                                </Typography>
                              </Stack>
                            </Stack>
                            {isSelected ? (
                              <CheckCircleOutlineIcon color="primary" fontSize="small" />
                            ) : (
                              <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                            )}
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Stack>
              ) : null}
              {isManualWabaInputVisible && manualValidationError ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {manualValidationError}
                </Alert>
              ) : null}
            </Stack>
          )}
        </Stack>
      </Box>

      {metaConnectionMode === "embedded" && !hasMetaEmbeddedSignupConfig && (
        <Alert severity="warning">
          Configure `VITE_META_APP_ID` e
          `VITE_META_EMBEDDED_SIGNUP_CONFIG_ID`.
        </Alert>
      )}
      {metaConnectionMode === "embedded" && hasMetaEmbeddedSignupConfig && !isSecureContextForMeta && (
        <Alert severity="warning">
          O login da Meta exige HTTPS (ou localhost).
        </Alert>
      )}
      {metaConnectionMode === "embedded" && metaEmbeddedSignupError && (
        <Alert severity="error">{metaEmbeddedSignupError}</Alert>
      )}
      {isMetaConnected && metaConnectionWabaId && (
        <Alert severity="success">
          WABA conectado: {metaConnectionWabaId}
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, minHeight: { xs: 0, md: 8 } }} />

      <Box sx={{ mt: "auto" }}>
        {showMetaDisconnectButton ? (
          <Button
            variant="outlined"
            color="inherit"
            type="button"
            onClick={onDisconnectMetaAccount}
            disabled={isSavingMetaConnection}
            fullWidth
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              px: 2.5,
            }}
          >
            {isSavingMetaConnection ? "Desvinculando..." : "Desvincular"}
          </Button>
        ) : metaConnectionMode === "manual" && hasManualWabaLookupSucceeded ? (
          <Button
            variant="contained"
            type="button"
            onClick={onManualLinking}
            disabled={
              !isFluxZapExtensionActive ||
              isSavingOption ||
              isSavingMetaConnection
            }
            fullWidth
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              px: 2.5,
            }}
          >
            Vincular manualmente
          </Button>
        ) : metaConnectionMode === "manual" ? null : (
          <Button
            variant="contained"
            type="button"
            onClick={onConnectMetaClick}
            disabled={
              !isFluxZapExtensionActive ||
              !hasMetaEmbeddedSignupConfig ||
              !isSecureContextForMeta ||
              !isMetaEmbeddedSignupReady ||
              isMetaEmbeddedSignupLoading ||
              isSavingOption ||
              isSavingMetaConnection
            }
            fullWidth
            startIcon={
              isMetaEmbeddedSignupLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FacebookIcon />
              )
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              px: 2.5,
            }}
          >
            {isMetaEmbeddedSignupLoading
              ? "Conectando..."
              : !isMetaEmbeddedSignupReady
                ? "Carregando SDK da Meta..."
                : "Vincular com Meta"}
          </Button>
        )}
      </Box>
    </Box>
  );
});

MetaOptionCard.displayName = "MetaOptionCard";

export default MetaOptionCard;
